/**
 * Mock Data Generator for Nexus Demo
 *
 * Generates realistic mock data for agents, conversations, analytics,
 * and integrations to power the demo simulation system.
 */

import {
  NexusAgent,
  NexusConversation,
  NexusIntegration,
  AgentType,
  AgentStatus,
  ConversationType,
  CRMLead,
  CRMContact,
  CRMCompany,
  CRMOpportunity,
  CRMActivity,
  CRMLeadStatus,
  CRMLeadRating,
  CRMLeadSource,
  CRMContactType,
  CRMContactStatus,
  CRMOpportunityStage,
  CRMOpportunityType,
  CRMActivityType,
  CRMActivityStatus
} from '@/types/nexus'
import {
  NexusBooking,
  BookingService,
  BookingResource,
  BookingStatus,
  PaymentStatus,
  PricingModel,
  ResourceType
} from '@/types/booking'
import { createTranscriptFromMessages } from '@/lib/nexus/transcript-utils'

const DEMO_WORKSPACE_ID = 'demo-workspace-001'

// Realistic agent names and descriptions
const AGENT_NAMES = [
  'Restaurant Reservation Assistant',
  'Customer Support Agent',
  'Healthcare Appointment Scheduler',
  'Legal Intake Specialist',
  'PSW Check-In Agent',
  'Lead Qualification Bot',
  'E-commerce Support',
  'Technical Support Agent',
  'Sales Representative',
  'Billing Inquiry Assistant'
]

const AGENT_DESCRIPTIONS = [
  'Handles restaurant reservations, menu inquiries, and customer service',
  'Provides 24/7 customer support for product inquiries and issues',
  'Schedules medical appointments and answers patient questions',
  'Conducts initial client consultations and case assessments',
  'Performs automated check-ins with personal support workers',
  'Qualifies leads and schedules sales demos',
  'Assists with online orders, returns, and product questions',
  'Troubleshoots technical issues and provides solutions',
  'Handles sales inquiries and schedules consultations',
  'Manages billing questions and payment processing'
]

const SYSTEM_PROMPTS = [
  'You are a friendly and professional restaurant reservation assistant. Help customers book tables, answer menu questions, and handle special requests. Always be courteous and confirm reservation details.',
  'You are a helpful customer support agent. Listen carefully to customer concerns, provide clear solutions, and escalate complex issues when needed. Maintain a positive and empathetic tone.',
  'You are a healthcare appointment scheduler. Help patients find available appointment times, answer questions about services, and provide important pre-appointment information. Always maintain patient confidentiality.',
  'You are a legal intake specialist. Conduct initial consultations with potential clients, gather relevant case information, and schedule follow-up meetings with attorneys. Be professional and empathetic.',
  'You are a PSW check-in agent. Perform automated welfare checks with personal support workers, verify their status, and report any concerns. Be supportive and understanding.',
  'You are a lead qualification specialist. Ask qualifying questions to understand prospect needs, determine fit, and schedule appropriate sales demos. Be consultative, not pushy.',
  'You are an e-commerce support agent. Help customers with orders, returns, shipping questions, and product information. Provide accurate and timely assistance.',
  'You are a technical support agent. Troubleshoot technical issues systematically, provide step-by-step solutions, and escalate when necessary. Be patient and clear.',
  'You are a sales representative. Understand customer needs, present relevant solutions, and guide prospects through the sales process. Be consultative and helpful.',
  'You are a billing inquiry assistant. Help customers understand their bills, process payments, and resolve billing disputes. Be clear, accurate, and patient.'
]

// Supported voices/providers for demo data should stay within the app's
// `VoiceProvider` union and voice option set.
const DEMO_VOICE_PROVIDER = 'openai' as const

const DEMO_OPENAI_REALTIME_VOICES = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer'
] as const

type DemoOpenAIVoice = (typeof DEMO_OPENAI_REALTIME_VOICES)[number]

// Generate mock agents
export function generateMockAgents(): NexusAgent[] {
  const agents: NexusAgent[] = []
  const types: AgentType[] = ['voice', 'chat', 'hybrid']
  const statuses: AgentStatus[] = ['active', 'active', 'active', 'inactive', 'paused']
  const models = ['losi-omega', 'losi-mini', 'losi-omega', 'losi-mini']
  const voices: DemoOpenAIVoice[] = [...DEMO_OPENAI_REALTIME_VOICES]

  for (let i = 0; i < 8; i++) {
    const type = types[i % types.length]
    const status = statuses[i % statuses.length]
    const model = models[i % models.length]
    const voice = voices[i % voices.length]
    const name = AGENT_NAMES[i]
    const description = AGENT_DESCRIPTIONS[i]
    const systemPrompt = SYSTEM_PROMPTS[i]

    agents.push({
      id: `demo-agent-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      name,
      type,
      model_id: model,
      system_prompt: systemPrompt,
      voice_settings:
        type === 'voice' || type === 'hybrid'
          ? {
              provider: DEMO_VOICE_PROVIDER,
              voice,
              speed: 1.0,
              temperature: 0.8,
              modalities: ['text', 'audio']
            }
          : undefined,
      capabilities: {
        tools: [],
        integrations: [],
        actions: []
      },
      status,
      created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      metadata: {
        description,
        industry: ['restaurant', 'healthcare', 'legal', 'psw', 'customer_care'][i % 5]
      }
    })
  }

  return agents
}

// Generate mock conversations with enhanced transcripts
export function generateMockConversations(agentIds: string[]): NexusConversation[] {
  const conversations: NexusConversation[] = []
  const types: ConversationType[] = ['voice', 'text', 'hybrid']

  // Enhanced conversation transcripts with more realistic content
  const transcripts = [
    [
      { role: 'assistant', content: 'Hello! Thank you for calling. How can I help you today?' },
      { role: 'user', content: "I'd like to make a reservation for tonight at 7pm" },
      {
        role: 'assistant',
        content: "I'd be happy to help you make a reservation. For how many people?"
      },
      { role: 'user', content: 'Four people, please' },
      {
        role: 'assistant',
        content:
          'Perfect! I have a table available for 4 people at 7pm tonight. May I have your name and phone number to confirm?'
      },
      { role: 'user', content: "Sure, it's John Smith, 555-1234" },
      {
        role: 'assistant',
        content:
          'Great! Your reservation is confirmed for 4 people at 7pm tonight. We look forward to seeing you!'
      }
    ],
    [
      {
        role: 'assistant',
        content:
          "Hi! I'm here to help with any questions or issues you might have. What can I assist you with today?"
      },
      { role: 'user', content: 'I need help with my recent order' },
      {
        role: 'assistant',
        content: "I'd be happy to help with your order. Can you provide me with your order number?"
      },
      { role: 'user', content: "It's #12345" },
      {
        role: 'assistant',
        content:
          "I found your order. It was shipped yesterday and should arrive by tomorrow. Is there something specific you'd like to know about it?"
      },
      { role: 'user', content: 'Can I change the delivery address?' },
      {
        role: 'assistant',
        content:
          "I can help with that. Unfortunately, since the order has already shipped, I'll need to contact the carrier directly. Let me do that for you right away."
      }
    ],
    [
      {
        role: 'assistant',
        content:
          'Hello! I can help you schedule an appointment. What type of appointment are you looking to book?'
      },
      { role: 'user', content: 'I need a routine checkup' },
      {
        role: 'assistant',
        content: 'I can help you schedule a routine checkup. What days and times work best for you?'
      },
      { role: 'user', content: 'Any weekday morning would work' },
      {
        role: 'assistant',
        content:
          'I have availability next Tuesday at 10am or Wednesday at 9am. Which would you prefer?'
      },
      { role: 'user', content: 'Tuesday at 10am sounds good' },
      {
        role: 'assistant',
        content:
          "Perfect! I've scheduled your routine checkup for Tuesday at 10am. You'll receive a confirmation email shortly."
      }
    ],
    [
      { role: 'assistant', content: 'Good morning! How can I assist you today?' },
      { role: 'user', content: "I'm interested in learning more about your services" },
      {
        role: 'assistant',
        content: "I'd be happy to help! What specific services are you interested in?"
      },
      { role: 'user', content: 'I need help with lead generation and customer outreach' },
      {
        role: 'assistant',
        content:
          'Great! We offer comprehensive CRM solutions with AI-powered outreach. Would you like to schedule a demo to see how it works?'
      },
      { role: 'user', content: 'Yes, that would be helpful' },
      {
        role: 'assistant',
        content: 'Perfect! I can schedule you for a 30-minute demo. What day works best for you?'
      }
    ],
    [
      {
        role: 'assistant',
        content: 'Hello! Thank you for reaching out. What can I help you with?'
      },
      { role: 'user', content: 'I have a billing question' },
      {
        role: 'assistant',
        content: 'I can help with that. Can you provide your account number or email address?'
      },
      { role: 'user', content: "Sure, it's user@example.com" },
      { role: 'assistant', content: 'I found your account. What billing question do you have?' },
      { role: 'user', content: "I was charged twice for last month's subscription" },
      {
        role: 'assistant',
        content:
          'I apologize for the inconvenience. Let me review your account and process a refund for the duplicate charge right away.'
      }
    ],
    // Dental office - after hours
    [
      { role: 'assistant', content: "Thank you for calling Dr. Chen's Dental Office. We're currently closed, but I'm the AI assistant and can help you 24/7. How can I assist you?" },
      { role: 'user', content: "I have a really bad toothache and I need to see someone as soon as possible" },
      { role: 'assistant', content: "I'm sorry to hear you're in pain. Let me check our earliest available emergency appointment. Are you an existing patient with us?" },
      { role: 'user', content: "Yes, my name is Margaret Chen, date of birth March 15, 1985" },
      { role: 'assistant', content: "I found your file, Margaret. I can see Dr. Chen has an opening tomorrow morning at 8:30 AM for emergency appointments. Would you like me to book that for you?" },
      { role: 'user', content: "Yes please, that would be great" },
      { role: 'assistant', content: "Done! I've booked you for an emergency dental appointment tomorrow at 8:30 AM with Dr. Chen. You'll receive a confirmation text shortly. In the meantime, you can take over-the-counter ibuprofen for the pain. Is there anything else I can help with?" },
      { role: 'user', content: "No, that's perfect. Thank you so much!" },
      { role: 'assistant', content: "You're welcome, Margaret! We'll see you tomorrow at 8:30 AM. Feel better soon!" }
    ],
    // Law firm intake
    [
      { role: 'assistant', content: "Good afternoon, thank you for calling Morrison & Associates. I'm the firm's AI intake specialist. How may I direct your call?" },
      { role: 'user', content: "Hi, I was in a car accident last week and I think I need a lawyer" },
      { role: 'assistant', content: "I'm sorry to hear about your accident. I can help gather some initial information for our personal injury team. Were there any injuries involved?" },
      { role: 'user', content: "Yes, I have whiplash and the other driver ran a red light" },
      { role: 'assistant', content: "I understand. A few quick questions: Do you have a police report number? And have you received medical treatment for the whiplash?" },
      { role: 'user', content: "Yes, report number is 2024-45678, and I've been to the ER and my chiropractor twice" },
      { role: 'assistant', content: "Thank you. I've created a new case intake file with those details. Based on what you've described, this falls within our personal injury practice. Attorney Sarah Morrison has availability for a free consultation this Thursday at 2 PM or Friday at 10 AM. Which works better?" },
      { role: 'user', content: "Thursday at 2 works" },
      { role: 'assistant', content: "I've scheduled your free consultation with Attorney Morrison for Thursday at 2 PM. I'll send you a confirmation email with our office address and a list of documents to bring. The consultation is completely free and there's no obligation. Is there anything else?" }
    ],
    // Auto dealership service
    [
      { role: 'assistant', content: "Welcome to Prestige Auto Service! I can help you schedule maintenance, check on a repair, or answer questions about your vehicle. What can I help with?" },
      { role: 'user', content: "I need to schedule an oil change and tire rotation for my 2022 BMW X5" },
      { role: 'assistant', content: "I'd be happy to help! Let me pull up our service schedule. I can see we have openings this Saturday morning at 9 AM or Monday afternoon at 3 PM. The oil change and tire rotation package for your X5 is $189. Which time works better?" },
      { role: 'user', content: "Saturday at 9 AM" },
      { role: 'assistant', content: "Perfect! I've booked your BMW X5 for an oil change and tire rotation this Saturday at 9 AM. Estimated service time is about 90 minutes. We also have a complimentary shuttle service if you need a ride while you wait. Would you like me to arrange that?" },
      { role: 'user', content: "That would be great, yes please" },
      { role: 'assistant', content: "Done! Shuttle is arranged. You'll receive a text reminder Friday evening. And just a heads up — your vehicle's records show you're due for a brake inspection at your next visit. Would you like me to add that to Saturday's service?" },
      { role: 'user', content: "Sure, add that too" },
      { role: 'assistant', content: "Added! Your Saturday appointment now includes oil change, tire rotation, and brake inspection. Total estimate is $249. See you Saturday at 9 AM!" }
    ],
    // Salon booking with preferences
    [
      { role: 'assistant', content: "Hi! Thanks for reaching out to Belle Hair Studio. I can help you book an appointment. What service are you looking for?" },
      { role: 'user', content: "I'd like a haircut and color with Jessica" },
      { role: 'assistant', content: "Great choice! Jessica is one of our top stylists. Let me check her availability. What type of color service — full highlights, balayage, or single process?" },
      { role: 'user', content: "Balayage please" },
      { role: 'assistant', content: "A haircut plus balayage with Jessica typically takes about 3 hours. She has openings next Tuesday starting at 10 AM, or Thursday at 1 PM. Her balayage starts at $225 plus the haircut at $65. Would either of those work?" },
      { role: 'user', content: "Tuesday at 10 works great" },
      { role: 'assistant', content: "You're all set! I've booked you with Jessica on Tuesday at 10 AM for a haircut and balayage. Total estimate: $290 plus tax. Remember to arrive with clean, dry hair for the best balayage results. I'll send you a reminder Monday evening!" }
    ]
  ]

  const names = [
    'Margaret Chen',
    'David Rodriguez',
    'Sarah Johnson',
    'Michael O\'Brien',
    'Lisa Patel',
    'Robert Taylor',
    'Jennifer Martinez',
    'James Wilson'
  ]
  const phones = [
    '+1-555-0100',
    '+1-555-0101',
    '+1-555-0102',
    '+1-555-0103',
    '+1-555-0104',
    '+1-555-0105',
    '+1-555-0106',
    '+1-555-0107'
  ]
  const topics = [
    'reservation',
    'order inquiry',
    'appointment',
    'support issue',
    'billing question',
    'product information',
    'technical support',
    'sales inquiry',
    'emergency dental appointment',
    'personal injury intake'
  ]

  const summaries = [
    'Inbound call — customer booked a table for 4 at 7 PM. Reservation confirmed, SMS sent.',
    'Chat session — resolved order tracking query (#12345). Address change escalated to carrier.',
    'Voice call — scheduled routine checkup for Tuesday at 10 AM. Confirmation email sent.',
    'Inbound inquiry — customer interested in CRM services. Demo scheduled for next week.',
    'Billing support — identified duplicate charge. Refund of $49.99 processed automatically.',
    'After-hours call — emergency dental appointment booked for next morning 8:30 AM.',
    'Legal intake — new personal injury case created. Free consultation scheduled with Attorney Morrison.',
    'Service scheduling — BMW X5 oil change + tire rotation + brake inspection booked for Saturday 9 AM. Shuttle arranged.',
    'Salon booking — haircut and balayage with Jessica booked for Tuesday 10 AM. Revenue: $290.',
    'Follow-up call — confirmed tomorrow\'s appointment. Customer rated experience 5/5.'
  ]

  for (let i = 0; i < 30; i++) {
    const agentId = agentIds[i % agentIds.length]
    const type = types[i % types.length]
    const transcript = transcripts[i % transcripts.length]
    const duration = type === 'voice' ? Math.floor(Math.random() * 300 + 60) : undefined // 1-6 minutes for voice
    const startedAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
    const endedAt = duration
      ? new Date(startedAt.getTime() + duration * 1000)
      : new Date(startedAt.getTime() + Math.random() * 60000)
    const nameIndex = i % names.length

    const transcriptStr = createTranscriptFromMessages(
      transcript.map((m) => ({ speaker: m.role as 'user' | 'assistant', content: m.content }))
    )
    conversations.push({
      id: `demo-conversation-${String(i + 1).padStart(3, '0')}`,
      agent_id: agentId,
      conversation_type: type,
      participant_info: {
        name: names[nameIndex],
        phone: phones[nameIndex],
        email: `user${i + 1}@example.com`
      },
      started_at: startedAt.toISOString(),
      ended_at: endedAt.toISOString(),
      duration_seconds: duration,
      transcript: transcriptStr,
      summary: summaries[i % summaries.length],
      call_direction: i % 4 === 0 ? 'outbound' : 'inbound',
      call_outcome: ['resolved', 'appointment_booked', 'transferred', 'follow_up_needed', 'resolved'][i % 5],
      actions_taken: i % 3 === 0 ? [
        { action_type: 'booking_created', timestamp: endedAt.toISOString(), status: 'success' as const },
        { action_type: 'crm_contact_updated', timestamp: endedAt.toISOString(), status: 'success' as const }
      ] : i % 3 === 1 ? [
        { action_type: 'lead_created', timestamp: endedAt.toISOString(), status: 'success' as const }
      ] : undefined
    })
  }

  return conversations.sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  )
}

// Generate mock analytics
export function generateMockAnalytics() {
  const now = new Date()
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(now)
    date.setDate(date.getDate() - (6 - i))
    return {
      date: date.toISOString().split('T')[0],
      conversations: Math.floor(Math.random() * 50 + 20),
      duration: Math.floor(Math.random() * 200 + 100),
      resolution_rate: Math.random() * 0.2 + 0.75 // 75-95%
    }
  })

  return {
    total_conversations: 342,
    total_duration_minutes: 1245,
    average_duration_minutes: 3.6,
    resolution_rate: 0.87,
    active_agents: 6,
    total_agents: 8,
    daily_stats: last7Days,
    agent_performance: [
      {
        agent_id: 'demo-agent-001',
        name: 'Restaurant Reservation Assistant',
        conversations: 45,
        resolution_rate: 0.91
      },
      {
        agent_id: 'demo-agent-002',
        name: 'Customer Support Agent',
        conversations: 67,
        resolution_rate: 0.88
      },
      {
        agent_id: 'demo-agent-003',
        name: 'Healthcare Appointment Scheduler',
        conversations: 52,
        resolution_rate: 0.94
      },
      {
        agent_id: 'demo-agent-004',
        name: 'Legal Intake Specialist',
        conversations: 38,
        resolution_rate: 0.85
      },
      {
        agent_id: 'demo-agent-005',
        name: 'PSW Check-In Agent',
        conversations: 89,
        resolution_rate: 0.92
      },
      {
        agent_id: 'demo-agent-006',
        name: 'Lead Qualification Bot',
        conversations: 51,
        resolution_rate: 0.79
      }
    ],
    cost_estimate: {
      total: 124.5,
      per_conversation: 0.36,
      currency: 'USD'
    }
  }
}

// Generate mock integrations
export function generateMockIntegrations(): NexusIntegration[] {
  return [
    {
      id: 'demo-integration-001',
      workspace_id: DEMO_WORKSPACE_ID,
      integration_type: 'rest_api',
      name: 'Google Calendar',
      status: 'connected',
      connection_config: {
        api_key: 'demo-key-***',
        custom: { api_key: 'demo-key-***' }
      },
      created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'demo-integration-002',
      workspace_id: DEMO_WORKSPACE_ID,
      integration_type: 'rest_api',
      name: 'Slack',
      status: 'connected',
      connection_config: {
        server_url: 'https://hooks.slack.com/***',
        custom: { webhook_url: 'https://hooks.slack.com/***' }
      },
      created_at: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'demo-integration-003',
      workspace_id: DEMO_WORKSPACE_ID,
      integration_type: 'rest_api',
      name: 'Zendesk',
      status: 'connected',
      connection_config: {
        custom: { subdomain: 'demo', api_token: '***' }
      },
      created_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'demo-integration-004',
      workspace_id: DEMO_WORKSPACE_ID,
      integration_type: 'rest_api',
      name: 'Mautic',
      status: 'connected',
      connection_config: {
        server_url: 'https://demo.mautic.com/api',
        api_key: 'demo-mautic-key-***'
      },
      created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
}

// Generate mock booking services
export function generateMockBookingServices(): BookingService[] {
  const services: BookingService[] = []
  const serviceNames = [
    'Initial Consultation',
    'Follow-up Appointment',
    'Technical Support Session',
    'Sales Demo',
    'Product Training',
    'Strategy Session'
  ]
  const durations = [30, 60, 45, 60, 90, 60]
  const prices = [150, 200, 100, 0, 300, 250]

  for (let i = 0; i < serviceNames.length; i++) {
    services.push({
      id: `demo-service-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      name: serviceNames[i],
      description: `${serviceNames[i]} service for customer engagement`,
      duration_minutes: durations[i],
      buffer_before_minutes: 5,
      buffer_after_minutes: 10,
      price_model: prices[i] === 0 ? 'free' : ('fixed' as PricingModel),
      base_price: prices[i],
      price_config: {},
      currency: 'USD',
      video_enabled: i % 2 === 0,
      video_provider: i % 2 === 0 ? 'google_meet' : 'none',
      metadata: {},
      is_active: true,
      created_at: new Date(Date.now() - (30 - i) * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - (30 - i) * 24 * 60 * 60 * 1000).toISOString()
    })
  }

  return services
}

// Generate mock booking resources
export function generateMockBookingResources(): BookingResource[] {
  const resources: BookingResource[] = []
  const resourceNames = [
    'Dr. Sarah Johnson',
    'John Smith - Sales',
    'Emily Davis - Support',
    'Mike Wilson - Technical',
    'Conference Room A'
  ]
  const types: ResourceType[] = ['staff', 'staff', 'staff', 'staff', 'room']

  const defaultAvailability = {
    monday: [{ start: '09:00', end: '17:00' }],
    tuesday: [{ start: '09:00', end: '17:00' }],
    wednesday: [{ start: '09:00', end: '17:00' }],
    thursday: [{ start: '09:00', end: '17:00' }],
    friday: [{ start: '09:00', end: '17:00' }]
  }

  for (let i = 0; i < resourceNames.length; i++) {
    resources.push({
      id: `demo-resource-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      name: resourceNames[i],
      description: `${resourceNames[i]} availability`,
      type: types[i],
      availability_json: defaultAvailability,
      timezone: 'America/New_York',
      metadata: {},
      created_at: new Date(Date.now() - (30 - i) * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(Date.now() - (30 - i) * 24 * 60 * 60 * 1000).toISOString()
    })
  }

  return resources
}

// Generate mock bookings
export function generateMockBookings(serviceIds: string[], resourceIds: string[]): NexusBooking[] {
  const bookings: NexusBooking[] = []
  const statuses: BookingStatus[] = [
    'confirmed',
    'confirmed',
    'tentative',
    'completed',
    'cancelled',
    'paid'
  ]
  const paymentStatuses: PaymentStatus[] = ['paid', 'unpaid', 'paid', 'paid', 'refunded', 'paid']
  const customerNames = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams', 'David Brown']
  const customerPhones = ['+1-555-0100', '+1-555-0101', '+1-555-0102', '+1-555-0103', '+1-555-0104']
  const customerEmails = [
    'john@example.com',
    'jane@example.com',
    'mike@example.com',
    'sarah@example.com',
    'david@example.com'
  ]

  for (let i = 0; i < 15; i++) {
    const serviceId = serviceIds[i % serviceIds.length]
    const resourceId = resourceIds[i % resourceIds.length]
    const status = statuses[i % statuses.length]
    const paymentStatus = paymentStatuses[i % paymentStatuses.length]
    const customerIndex = i % customerNames.length

    // Generate start time in the next 30 days
    const startTime = new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000)
    startTime.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 4) * 15, 0, 0)
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000) // 1 hour duration

    bookings.push({
      id: `demo-booking-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      service_id: serviceId,
      resource_id: resourceId,
      title: `Booking ${i + 1}`,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      timezone: 'America/New_York',
      status,
      customer_name: customerNames[customerIndex],
      customer_email: customerEmails[customerIndex],
      customer_phone: customerPhones[customerIndex],
      price_amount: Math.floor(Math.random() * 200 + 100),
      currency: 'USD',
      payment_status: paymentStatus,
      metadata: {},
      created_at: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString()
    })
  }

  return bookings.sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  )
}

// Generate mock CRM leads
export function generateMockCRMLeads(): CRMLead[] {
  const leads: CRMLead[] = []
  const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Emily', 'Robert', 'Lisa']
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore']
  const companies = [
    'Acme Corp',
    'Tech Solutions Inc',
    'Global Industries',
    'Digital Services',
    'Innovation Labs',
    'Future Systems',
    'Smart Solutions',
    'NextGen Tech'
  ]
  const statuses: CRMLeadStatus[] = [
    'new',
    'contacted',
    'qualified',
    'propose',
    'negotiation',
    'closed_won',
    'closed_lost'
  ]
  const ratings: CRMLeadRating[] = ['hot', 'warm', 'cold', 'unrated']
  const sources: CRMLeadSource[] = [
    'website',
    'referral',
    'cold_call',
    'social_media',
    'trade_show'
  ]

  for (let i = 0; i < 20; i++) {
    const firstName = firstNames[i % firstNames.length]
    const lastName = lastNames[i % lastNames.length]
    const company = companies[i % companies.length]
    const status = statuses[i % statuses.length]
    const rating = ratings[i % ratings.length]
    const source = sources[i % sources.length]
    const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)

    leads.push({
      id: `demo-lead-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      first_name: firstName,
      last_name: lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      phone: `+1-555-${String(1000 + i).padStart(4, '0')}`,
      company,
      status,
      rating,
      source,
      probability: Math.floor(Math.random() * 100),
      estimated_value: Math.floor(Math.random() * 50000 + 5000),
      tags: ['demo', 'qualified'],
      custom_fields: {},
      notes_count: Math.floor(Math.random() * 5),
      activities_count: Math.floor(Math.random() * 10),
      score: Math.floor(Math.random() * 100),
      created_at: createdAt.toISOString(),
      updated_at: new Date(
        createdAt.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString()
    })
  }

  return leads
}

// Generate mock CRM contacts
export function generateMockCRMContacts(): CRMContact[] {
  const contacts: CRMContact[] = []
  const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Emily', 'Robert', 'Lisa']
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore']
  const companies = ['Acme Corp', 'Tech Solutions Inc', 'Global Industries', 'Digital Services']
  const types: CRMContactType[] = ['prospect', 'customer', 'partner', 'vendor']
  const statuses: CRMContactStatus[] = ['new', 'contacted', 'qualified', 'nurturing', 'converted']

  for (let i = 0; i < 15; i++) {
    const firstName = firstNames[i % firstNames.length]
    const lastName = lastNames[i % lastNames.length]
    const company = companies[i % companies.length]
    const type = types[i % types.length]
    const status = statuses[i % statuses.length]
    const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)

    contacts.push({
      id: `demo-contact-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      first_name: firstName,
      last_name: lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      phone: `+1-555-${String(2000 + i).padStart(4, '0')}`,
      company,
      type,
      status,
      tags: ['demo'],
      custom_fields: {},
      preferred_contact_method: 'email',
      language: 'en',
      do_not_contact: false,
      gdpr_consent: true,
      created_at: createdAt.toISOString(),
      updated_at: new Date(
        createdAt.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString()
    })
  }

  return contacts
}

// Generate mock CRM companies
export function generateMockCRMCompanies(): CRMCompany[] {
  const companies: CRMCompany[] = []
  const companyNames = [
    'Acme Corp',
    'Tech Solutions Inc',
    'Global Industries',
    'Digital Services',
    'Innovation Labs',
    'Future Systems'
  ]
  const industries = ['Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 'Services']
  const sizes: Array<'1-10' | '11-50' | '51-200' | '201-500' | '501-1000' | '1000+'> = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ]

  for (let i = 0; i < companyNames.length; i++) {
    const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)

    companies.push({
      id: `demo-company-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      name: companyNames[i],
      domain: `${companyNames[i].toLowerCase().replace(/\s+/g, '')}.com`,
      website: `https://${companyNames[i].toLowerCase().replace(/\s+/g, '')}.com`,
      phone: `+1-555-${String(3000 + i).padStart(4, '0')}`,
      industry: industries[i],
      company_size: sizes[i],
      tags: ['demo'],
      custom_fields: {},
      created_at: createdAt.toISOString(),
      updated_at: new Date(
        createdAt.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString()
    })
  }

  return companies
}

// Generate mock CRM opportunities
export function generateMockCRMOpportunities(
  leadIds: string[],
  contactIds: string[],
  companyIds: string[]
): CRMOpportunity[] {
  const opportunities: CRMOpportunity[] = []
  const names = [
    'Enterprise License',
    'Annual Subscription',
    'Custom Integration',
    'Premium Package',
    'Partnership Deal'
  ]
  const stages: CRMOpportunityStage[] = [
    'prospecting',
    'qualification',
    'needs_analysis',
    'value_proposition',
    'proposal',
    'negotiation',
    'closed_won'
  ]
  const types: CRMOpportunityType[] = [
    'new_business',
    'existing_business',
    'partnership',
    'renewal'
  ]

  for (let i = 0; i < 10; i++) {
    const createdAt = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)
    const expectedClose = new Date(createdAt.getTime() + Math.random() * 90 * 24 * 60 * 60 * 1000)

    opportunities.push({
      id: `demo-opportunity-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      name: names[i % names.length],
      amount: Math.floor(Math.random() * 100000 + 10000),
      currency: 'USD',
      stage: stages[i % stages.length],
      type: types[i % types.length],
      probability: Math.floor(Math.random() * 100),
      expected_close_date: expectedClose.toISOString(),
      lead_id: leadIds[i % leadIds.length],
      contact_id: contactIds[i % contactIds.length],
      company_id: companyIds[i % companyIds.length],
      tags: ['demo'],
      custom_fields: {},
      created_at: createdAt.toISOString(),
      updated_at: new Date(
        createdAt.getTime() + Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString()
    })
  }

  return opportunities
}

// Generate mock CRM activities
export function generateMockCRMActivities(contactIds: string[], leadIds: string[]): CRMActivity[] {
  const activities: CRMActivity[] = []
  const types: CRMActivityType[] = ['call', 'email', 'sms', 'meeting', 'note', 'follow_up']
  const statuses: CRMActivityStatus[] = ['completed', 'scheduled', 'cancelled']

  for (let i = 0; i < 25; i++) {
    const type = types[i % types.length]
    const status = statuses[i % statuses.length]
    const createdAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
    const isContact = i % 2 === 0

    activities.push({
      id: `demo-activity-${String(i + 1).padStart(3, '0')}`,
      workspace_id: DEMO_WORKSPACE_ID,
      activity_type: type,
      type,
      status,
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} with ${isContact ? 'contact' : 'lead'}`,
      description: `Demo ${type} activity`,
      contact_id: isContact ? contactIds[i % contactIds.length] : undefined,
      lead_id: !isContact ? leadIds[i % leadIds.length] : undefined,
      ai_generated: type.startsWith('ai_'),
      created_at: createdAt.toISOString(),
      updated_at: createdAt.toISOString()
    })
  }

  return activities.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
}
